import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { allArticles } from '@/mocks/articles';
import Footer from '../home/components/Footer';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug) || allArticles[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="bg-background-50">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-[1024px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="text-lg font-bold text-foreground-950 whitespace-nowrap tracking-tight cursor-pointer">
            Hipobuy <span className="text-primary-500">Spreadsheet</span>
          </Link>
          <div className="hidden md:flex items-center gap-4">
            <Link to="/" className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i>Accueil
            </Link>
            <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">
              Guide Shopping
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm bg-primary-500 text-background-50 px-4 py-2 rounded-full font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Hipobuy Spreadsheet
            </a>
          </div>
        </div>
      </nav>

      <article className="max-w-[860px] mx-auto px-4 md:px-6 py-10 md:py-16">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Fil d'Ariane">
          <Link to="/" className="hover:text-foreground-700 transition-colors whitespace-nowrap">Accueil</Link>
          <i className="ri-arrow-right-s-line text-[10px]"></i>
          <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
        </nav>

        {/* Article Header */}
        <header className="mb-10">
          <span className="inline-block bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1.5 rounded-full mb-4">{article.tag}</span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5 leading-tight">
            {article.title}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl">
            {article.intro}
          </p>
          <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200">
            <div className="w-9 h-9 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center text-xs font-bold">
              HS
            </div>
            <div>
              <div className="text-sm font-medium text-foreground-900">Hipobuy Spreadsheet</div>
              <div className="text-xs text-foreground-500">Publié le 23 juin 2026 · 12 min de lecture</div>
            </div>
          </div>
        </header>

        {/* Article Hero Image */}
        <div className="mb-12 rounded-2xl overflow-hidden aspect-[16/7]">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Article Content Sections */}
        {article.sections.map((section, idx) => (
          <section key={idx} className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">{section.title}</h2>

            {section.content.split('\n\n').map((para, pi) => (
              <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {para}
              </p>
            ))}

            {/* Table if present */}
            {section.table && (
              <div className="my-6 overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      {section.table.headers.map((h, hi) => (
                        <th key={hi} className={`text-left p-3 md:p-4 font-semibold whitespace-nowrap ${hi === 0 ? 'text-foreground-700' : hi === 1 ? 'text-accent-700' : 'text-foreground-600'}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.table.rows.map((row, ri) => (
                      <tr key={ri} className="border-t border-background-200/50 hover:bg-background-50/70 transition-colors">
                        {row.map((cell, ci) => (
                          <td key={ci} className={`p-3 md:p-4 whitespace-nowrap ${ci === 0 ? 'text-foreground-800 font-medium' : ci === 1 ? 'text-accent-700' : 'text-foreground-600'}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}

        {/* Inline CTA */}
        <div className="my-10 bg-accent-50 border border-accent-200/60 rounded-2xl p-6 md:p-8 text-center">
          <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-2">
            Prêt à transformer vos données e-commerce ?
          </h3>
          <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto leading-relaxed">
            Découvrez le hipobuy spreadsheet et passez à l&apos;analyse de données professionnelle dès aujourd&apos;hui.
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            Accéder au Hipobuy Spreadsheet
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </div>

        {/* FAQ Section */}
        {article.faq && article.faq.length > 0 && (
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              Questions Fréquentes sur le Hipobuy Spreadsheet
            </h2>
            <div className="space-y-3">
              {article.faq.map((item, idx) => (
                <details key={idx} className="group border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer hover:bg-background-50/50 transition-colors list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">{item.question}</span>
                    <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Conclusion */}
        <section className="mb-10">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">Conclusion</h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
            {article.conclusion}
          </p>
        </section>

        {/* Bottom internal links & CTA */}
        <div className="border-t border-background-200 pt-8 mt-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 font-medium transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line mr-1"></i>Retour à l&apos;accueil
              </Link>
              <span className="text-foreground-300 hidden sm:inline">|</span>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
              >
                Visiter le site principal →
              </a>
            </div>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-background-50 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </article>

      {/* Schema.org Article structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.intro,
            "datePublished": "2026-06-23",
            "author": { "@type": "Organization", "name": "Hipobuy Spreadsheet" },
            "publisher": { "@type": "Organization", "name": "Hipobuy Spreadsheet" },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": import.meta.env.VITE_SITE_URL + "/article/" + article.slug
            }
          })
        }}
      />

      {/* Schema.org BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": import.meta.env.VITE_SITE_URL + "/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": article.title,
                "item": import.meta.env.VITE_SITE_URL + "/article/" + article.slug
              }
            ]
          })
        }}
      />

      {/* FAQ Schema.org */}
      {article.faq && article.faq.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": article.faq.map((item) => ({
                "@type": "Question",
                "name": item.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": item.answer,
                },
              })),
            })
          }}
        />
      )}

      <Footer />
    </div>
  );
}